import { cloneDeep } from 'lodash';
import { useEffect, useMemo } from 'react';

import { ErpConversionGroupByType, ErpObject } from '../types';

import { useApplyArticlePricesOnPositions } from './use-apply-article-prices-on-positions';

/**
 * Custom hook to apply prices to ERP data.
 *
 * @param fetchedData - The current fetched data.
 * @param setPatchToData - The setter function for the data state.
 * @param groupByType - The group by type.
 */

export const useApplyPrices = (
  fetchedData: ErpObject[],
  setPatchToData: React.Dispatch<React.SetStateAction<ErpObject[]>>,
  groupByType?: ErpConversionGroupByType
): void => {
  const positions = useMemo(
    () => fetchedData.flatMap((x) => x.positionList),
    [fetchedData]
  );
  const { applyArticlePricesOnPositions, loading } =
    useApplyArticlePricesOnPositions({
      positions,
      groupByType,
      sourcePrices: true,
    });

  // This effect applies the prices to the ERP data.
  useEffect(() => {
    if (groupByType === 'NONE') return;
    if (!loading) {
      setPatchToData((prev) => {
        // Create a deep clone of the previous state to ensure immutability.
        const newData = cloneDeep(prev);

        // Iterate over each ERP data item.
        newData.forEach((erpDataItem) => {
          // Map over the position list to apply patches.
          // Update the position list for the current ERP data item.
          erpDataItem.positionList = applyArticlePricesOnPositions(
            erpDataItem.positionList
          );
        });
        // Return the new, patched data.
        return newData;
      });
    }
  }, [
    fetchedData,
    setPatchToData,
    loading,
    applyArticlePricesOnPositions,
    groupByType,
  ]);
};
